import moment from 'moment';
import { cloneDeep, last, isEmpty } from 'lodash';

import { customPropsForNonAdminRoles } from '@/modules/reporting/services/ReportingService';

import { STATUS_ERROR } from '@/store/helpers';

import { getValueObject } from '@/modules/reporting/utils';
import {
  reportingService,
  savedInsightService,
  userSettingsService,
  callAnnotationService,
  callsService,
  filterService,
  tagLookupService,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';

// TODO: Move this function to utils
import {
  getInitialGroupingSettings,
  getDefaultInsightData,
  getDefaultCallLogsGridState,
  INSIGHT_REPORT_TYPE,
  CALLLOG_REPORT_TYPE,
} from './state';

import { booleanFormatter } from '../components/AgGridUtils';

import { defaultCallLogColDef, specificColsProperties } from '@/modules/reporting/callLogsDefaultColumnState';

import { defaultInsightColDefs } from '../insightsColumnState';

import {
  RELOAD_INSIGHTS_GRID_DATA,
  SET_INSIGHTS_COL_DEFS,
  SET_INSIGHTS_TAGS,
  SET_DEFAULT_INSIGHT_ACTIVE,
  SET_DEFAULT_CALLLOG_ACTIVE,
  SET_ACTIVE_INSIGHT,
  SET_ACTIVE_CALLLOG,
  SET_SAVED_INSIGHTS,
  SET_SAVED_INSIGHT,
  DUPLICATE_SAVED_INSIGHT,
  DELETE_SAVED_INSIGHT,
  SET_GROUP_BY_INSIGHT_SETTINGS,
  SET_SELECTED_GROUP_BY,
  SET_INSIGHT_FILTER_CRITERIA,
  PREPEND_AND_FILTER_CRITERIA,
  PREPEND_AND_FILTER_CRITERIA_TO_CALL_LOG,
  SET_INSIGHT_LAST_UPDATED_AT,
  SET_CALLLOG_NAME,
  SET_CALLLOG_FILTER_CRITERIA,
  SET_CALLLOG_DATE_RANGE,
  SET_CALLLOG_GRID_STATE,
  SET_CALLLOG_AUTO_RELOAD,
  SET_CALLLOG_SAVED_VISIBILITY,
  SET_INSIGHT_NAME,
  SET_INSIGHT_DATE_RANGE,
  SET_INSIGHT_AUTO_RELOAD,
  SET_INSIGHT_SAVED_VISIBILITY,
  SET_INSIGHT_GRID_STATE,
  SET_INSIGHT_SELECTED_CHART_TYPE,
  UPDATE_GRID_ROW_GROUPS,
  DELETE_GRID_ROW_GROUP,
  SET_GRID_ROW_GROUPS,
  RESET_INSIGHT_SETTINGS,
  SET_CHART_DRILLDOWN_STATE,
  SET_CHART_LOADER_STATUS,
  SET_CHART_DRILLDOWN_DATA,
  SET_CHART_DATA,
  SET_CHART_ALL_PARAMETERS,
  SET_CHART_SELECTED_PARAMETERS,
  SET_EXPORT_FOR_AG_GRID,
  SET_GROUP_BY_INSIGHT_DATA,
  SET_INSIGHT_TIMEZONE_ID,
  SET_CALLLOG_TIMEZONE_ID,
  SET_CALLLOG_HISTOGRAM_DATA,
  SET_CALLLOG_HISTOGRAM_LOADER_STATUS,
  SET_CALLLOG_HISTOGRAM_GROUPING,
  SET_CALL_LOGS_COL_DEFS,
  SET_INSIGHTS_DATA_CACHE,
  RELOAD_CALL_LOGS_GRID_DATA,
  SET_CALL_LOG_LAST_UPDATED_AT,
  SET_CALL_LOG_DETAILS,
  CLEAR_ACTIVE_INSIGHT_EXPANDED_GROUP_IDS,
  CLEAR_INSIGHTS_DATA_CACHE,
  SET_CALL_LOGS_VISIBILITY_SECTION,
  SET_CALL_LOGS_QUICK_INSIGHTS_DATA_CACHE,
  SET_CALL_LOGS_HISTOGRAM_DATA_CACHE,
  SET_TAG_AUTOCOMPLETE_VALUES,
  RESET_TAG_AUTOCOMPLETE_VALUES,
  SET_CALL_LOGS_VIEW_AS,
  SET_CALL_FIELD_AUTOCOMPLETE_VALUES,
  APPEND_TAG_AUTOCOMPLETE_VALUES,
  APPEND_CALL_FIELD_AUTOCOMPLETE_VALUES,
  RESET_CALL_FIELD_AUTOCOMPLETE_VALUES,
  RESET_CALL_LOG_DETAILS,
  SET_CALLLOG_QUICK_INSIGHTS_TAB,
  SET_CALL_LOG_EXPORT_LOADER_VISIBILITY,
  SET_CALL_LOGS_COL_DEFS_LOADED_FROM_API,
  SET_INSIGHTS_COL_DEFS_LOADED_FROM_API,
  SET_CALLLOG_QUICK_INSIGHTS_COLUMN_STATE,
} from './mutationTypes';

const DEFAULT_REPORT_ID = 'unsaved';

// TODO: Handle error responses in service
// - services return data only
// - error handling is done by services

export function isDefaultReportId(id) {
  return !id || id === DEFAULT_REPORT_ID;
}

function isInsightReport(reportType) {
  return typeof reportType === 'undefined'
      || reportType === INSIGHT_REPORT_TYPE;
}

function getVisibleColumnState(columnsState) {
  return columnsState.filter(column => !column.hide);
}

function colStateSame(oldState, newState ) {
  const same = oldState?.length === newState?.length && oldState.every((c, index) => {
    return c.colId === newState[index].colId
      && c.hide === newState[index].hide
      && c.pinned === newState[index].pinned
      && c.aggFunc === newState[index].aggFunc
      && c.pivotIndex === newState[index].pivotIndex
      && c.rowGroupIndex === newState[index].rowGroupIndex;
  });

  return same;
}

function setUserSettings(userSettingName) {
  userSettingsService.getUserSettings()
    .then((userSettings) => {
      userSettings[userSettingName] = 'no';
      return userSettingsService.saveUserSettings(userSettings);
    });
}


function getCallLogSummaryState(existingSettings, newSettings, role) {
  if (existingSettings?.[role]) {
    return {
      [role]: {
        selectTabGrouping: newSettings.selectTabGrouping || existingSettings[role].selectTabGrouping,
        columnState: newSettings.columnState || existingSettings[role].columnState
      }
    };
  }

  return {
    [role]: {
      selectTabGrouping: newSettings.selectTabGrouping,
      columnState: newSettings.columnState
    }
  };
}

const actions = {
  // action({ state, getters, commit, dispatch }, args)

  setCurrentView({ commit }, view) {
    commit('SET_CURRENT_VIEW', view);
  },

  getCallLogsColDefs({ state, rootState, commit }) {
    // TODO: Add better logic here, since we should get the columns from the source of truth which is the API
    if (state.callLog.colDefs.length <= defaultCallLogColDef.length) {
      reportingService.getCallLogsColDefs(rootState.currentAccountId).then((response) => {
        commit(SET_CALL_LOGS_COL_DEFS_LOADED_FROM_API, true);
        commit(SET_CALL_LOGS_COL_DEFS, response.data.columns);
      }).catch(() => {
        commit(SET_CALL_LOGS_COL_DEFS_LOADED_FROM_API, true);
      });
    }
  },

  setInsightsDataCache({ commit }, { data, cacheKey, totalsRecord, partialResult, lastUpdated }) {
    // TODO: Create map for caching data for different group keys
    commit(SET_INSIGHTS_DATA_CACHE, { data, cacheKey, totalsRecord, partialResult, lastUpdated });
  },

  clearInsightsDataCache({ commit }) {
    commit(CLEAR_INSIGHTS_DATA_CACHE);
  },

  getInsightsColDefs({ state, commit, rootState }) {
    // TODO: Add better logic here, since we should get the columns from the source of truth which is the API
    if (state.insight.colDefs.length <= defaultInsightColDefs.length) {
      reportingService.getInsightsColDefs(rootState.currentAccountId).then((response) => {
        commit(SET_INSIGHTS_COL_DEFS_LOADED_FROM_API, true);
        commit(SET_INSIGHTS_COL_DEFS, response.data.columns);
      }).catch(() => {
        commit(SET_INSIGHTS_COL_DEFS_LOADED_FROM_API, true);
      });
    }
  },

  async getTags({ commit, rootState, getters, state }, reportType) {
    let data = {
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    if (reportType === INSIGHT_REPORT_TYPE) {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeInsightTimeRange, getters.activeInsightTranslatedTimeZoneId),
      };
    } else {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      };
    }

    const response = await tagLookupService.getTagsForTimeRange(rootState.currentAccountId, data);

    const tags = response.data.tags || [];

    // Add add tags from the saved to the list to prevent columns dissapearing if the tag does not exist in a timerange
    const activeCallLog = state.callLog.reports[state.callLog.activeId];
    let savedTags = [];
    if (activeCallLog && activeCallLog.settings &&  activeCallLog.settings.gridSettings && activeCallLog.settings.gridSettings.columnState) {
      savedTags = activeCallLog.settings.gridSettings.columnState.filter(col => col.colId.includes('tag:')).map(col => {
        const [, tagType, tagName] = col.colId.split(':');
        return {
          tagName,
          tagType
        };
      });
    }

    commit(SET_INSIGHTS_TAGS, tags.concat(savedTags));
  },

  resetAutoCompleteValues({ commit }) {
    commit(RESET_CALL_FIELD_AUTOCOMPLETE_VALUES);
    commit(RESET_TAG_AUTOCOMPLETE_VALUES);
  },

  async getInsight({ commit, rootState, state, dispatch }, insightId) {
    let insight = state.savedInsights.filter(i => i.id === insightId);

    if (!insight || insight.length < 1) {
      // TODO: move this logic into VUEX state (state.savedInsights)
      const savedInsights = await savedInsightService.getInsights(rootState.currentAccountId);
      const savedInsight = savedInsights.filter(i => i.id === insightId);
      insight = savedInsight?.length > 0 ? savedInsight[0] : {};
      commit(SET_SAVED_INSIGHT, insight);
    } else {
      insight = insight[0];
    }

    if (insight.reportType === CALLLOG_REPORT_TYPE) {
      dispatch('addTagsAsColumnDefsFromColumnState', insight.settings.gridSettings.columnState);
    } else if (insight.reportType === INSIGHT_REPORT_TYPE) {
      dispatch('addTagsAsColumnDefsFromColumnState', insight.settings.groupSettings.gridState.columnState);
    }

    dispatch('setActiveReport', cloneDeep(insight));
  },

  async getSavedReports({ commit, rootState }) {
    const insights = await savedInsightService.getInsights(rootState.currentAccountId);
    commit(SET_SAVED_INSIGHTS, insights);
  },

  async saveReport({ commit, rootState, dispatch }, report) {
    const savedReport = await savedInsightService.saveReport(rootState.currentAccountId, report);
    commit(SET_SAVED_INSIGHT, savedReport);
    dispatch('setActiveReport', cloneDeep(savedReport));
  },

  async duplicateReport({ commit, rootState, dispatch }, report) {
    const duplicatedReport = await savedInsightService.duplicateReport(rootState.currentAccountId, report);
    commit(DUPLICATE_SAVED_INSIGHT, duplicatedReport);
    dispatch('setActiveReport', cloneDeep(duplicatedReport));
  },

  async deleteReport({ commit, rootState }, reportId) {
    await savedInsightService.deleteReport(rootState.currentAccountId, reportId);
    commit(DELETE_SAVED_INSIGHT, reportId);
  },

  setActiveReport({ dispatch }, report) {
    if (isInsightReport(report.reportType)) {
      dispatch('setActiveInsight', report);
    } else {
      dispatch('setActiveCallLog', report);
    }
  },

  setActiveInsight({ commit, dispatch, getters }, insight) {
    // Actions probably shouldn't be mutating anything. Here temporarily for backwards compatibility.
    insight.settings.groupSettings = insight.settings.groupSettings || insight.settings.groupBys[insight.settings.selectedGroupBy.field];
    // TODO: Figure out why we aren't caching the totals row (it's not showing up when pulled from cache [comment this out to see])

    commit(SET_ACTIVE_INSIGHT, insight);

    // Set this only if data is not set for the active insight
    if (!getters.activeInsightData) {
      commit(SET_GROUP_BY_INSIGHT_DATA, getDefaultInsightData());
    }

    dispatch('setSelectedChartType', insight.settings.selectedChartType);
  },

  setActiveCallLog({ commit }, callLog) {
    commit(SET_CALLLOG_HISTOGRAM_DATA, []);

    commit(SET_ACTIVE_CALLLOG, callLog);
  },

  setAllParameters({ commit, dispatch, getters }, { groupBy, allParameters }) {
    commit(SET_CHART_ALL_PARAMETERS, {
      groupBy,
      allParameters
    });

    if (!getters.activeInsight.settings.groupSettings.chartStates.selectedParameters) {
      dispatch('setSelectedParameters', { groupBy, selectedParameters: allParameters });
    }
  },

  setSelectedParameters({ commit }, { groupBy, selectedParameters }) {
    commit(SET_CHART_SELECTED_PARAMETERS, { groupBy, selectedParameters });
  },

  setFilterCriteria({ commit, dispatch }, { reportType, filterCriteria }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_FILTER_CRITERIA, filterCriteria);
      dispatch('refreshInsightGridData');
    } else {
      commit(SET_CALLLOG_FILTER_CRITERIA, filterCriteria);
      dispatch('refreshCallLogsData');
    }
  },

  resetFilterCriteria({ commit }, reportType) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_FILTER_CRITERIA, []);
    } else {
      commit(SET_CALLLOG_FILTER_CRITERIA, []);
    }
  },

  async refreshCallLogsData({ state, dispatch }) {
    dispatch('refreshCallLogGridData');

    if (state.callLog.sectionsVisibility['timeline']) {
      dispatch('getCallLogHistogramData');
    }
  },

  setDateRange({ commit }, { reportType, dateRangeDetails }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_DATE_RANGE, dateRangeDetails);
    } else {
      commit(SET_CALLLOG_DATE_RANGE, dateRangeDetails);
    }
  },

  addClickFilterCriteria({ commit, dispatch }, filterCriteria) {
    commit(PREPEND_AND_FILTER_CRITERIA, filterCriteria);
    dispatch('refreshInsightGridData');
  },

  setSavedReportVisibility({ commit }, { reportType, visibleAccountWide }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_SAVED_VISIBILITY, visibleAccountWide);
    } else {
      commit(SET_CALLLOG_SAVED_VISIBILITY, visibleAccountWide);
    }
  },

  setAutoReload({ commit }, { reportType, autoReload }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_AUTO_RELOAD, autoReload);
    } else {
      commit(SET_CALLLOG_AUTO_RELOAD, autoReload);
    }
  },

  setReportGridState({ commit }, { reportType, state }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_GRID_STATE, state);
    } else {
      commit(SET_CALLLOG_GRID_STATE, state);
    }
  },

  setCallSummaryColumnState({ commit }, columnState) {
    commit(SET_CALLLOG_QUICK_INSIGHTS_COLUMN_STATE, columnState);
  },

  saveCallSummaryDefaultStateToUserSettings({ getters, rootGetters }, { selectTabGrouping, columnState, isMobile }) {
    if (!getters.activeCallLogViewAsBuyerId && !getters.activeCallLogViewAsPubId && !getters.activeCallLogViewAsManagerId) {
      userSettingsService.getUserSettings().then((userSettings) => {
        let settings;

        if (isMobile) {
          settings = getCallLogSummaryState(userSettings.quickInsightsSettings?.mobile, { selectTabGrouping, columnState }, rootGetters.accountUserRole);
          settings = {
            mobile: settings
          };
        } else {
          settings = getCallLogSummaryState(userSettings.quickInsightsSettings, { selectTabGrouping, columnState }, rootGetters.accountUserRole);
        }

        userSettings.quickInsightsSettings = {
          ...userSettings.quickInsightsSettings,
          ...settings,
        };

        return userSettingsService.saveUserSettings(userSettings);
      });
    }
  },

  getCallSummaryDefaultState({ rootGetters, dispatch }, isMobile) {
    userSettingsService.getUserSettings().then((userSettings) => {
      let savedUserSettings;

      if (isMobile) {
        savedUserSettings = userSettings.quickInsightsSettings?.mobile?.[rootGetters.accountUserRole];
      } else {
        savedUserSettings = userSettings.quickInsightsSettings?.[rootGetters.accountUserRole];
      }

      if (savedUserSettings) {
        dispatch('setCallSummarySettings', savedUserSettings);
      }
    });
  },

  setCallSummarySettings({ commit }, settings) {
    commit(SET_CALLLOG_QUICK_INSIGHTS_TAB, settings.selectTabGrouping);
    commit(SET_CALLLOG_QUICK_INSIGHTS_COLUMN_STATE, settings.columnState);
  },

  saveCallLogDefaultGridStateToUserSettings({ getters, rootGetters }, { gridState, isMobile }) {
    if (!getters.activeCallLogViewAsBuyerId && !getters.activeCallLogViewAsPubId && !getters.activeCallLogViewAsManagerId) {
      userSettingsService.getUserSettings().then((userSettings) => {
        const newColState = getVisibleColumnState(gridState.columnState);

        if (gridState.columnState?.length > 1
          && (!colStateSame(userSettings.callLogsDefaultGridSettings?.columnState, newColState)
            || !colStateSame(userSettings.callLogsDefaultGridSettings?.[rootGetters.accountUserRole]?.columnState, newColState))
        ) {
          let localUserSettings = {};

          const saveGridState = {
            [rootGetters.accountUserRole]: {
              ...gridState,
              columnState: newColState
            },
          };

          if (isMobile) {
            localUserSettings.mobile = {
              ...localUserSettings.mobile,
              ...saveGridState
            };
          } else {
            localUserSettings = {
              ...saveGridState
            };
          }

          userSettings.callLogsDefaultGridSettings = {
            ...userSettings.callLogsDefaultGridSettings,
            ...localUserSettings
          };

          return userSettingsService.saveUserSettings(userSettings);
        }
      });
    }
  },

  getCallLogDefaultGridStateToUserSettings({ commit, dispatch, rootGetters }, isMobile) {
    userSettingsService.getUserSettings().then((userSettings) => {
      let savedUserSettings;

      if (isMobile && userSettings.callLogsDefaultGridSettings?.mobile?.[rootGetters.accountUserRole]?.columnState?.length > 1) {
        savedUserSettings = userSettings.callLogsDefaultGridSettings.mobile[rootGetters.accountUserRole];
      } else if (userSettings.callLogsDefaultGridSettings?.[rootGetters.accountUserRole]?.columnState?.length > 1) {
        savedUserSettings = userSettings.callLogsDefaultGridSettings[rootGetters.accountUserRole];
      } else if (userSettings.callLogsDefaultGridSettings?.columnState?.length > 1) {
        // This is getting state from the old structure, not used anymore
        savedUserSettings = userSettings.callLogsDefaultGridSettings;
      } else {
        dispatch('resetCallLogColumnState');
        return;
      }

      dispatch('addTagsAsColumnDefsFromColumnState', savedUserSettings.columnState);

      commit(SET_CALLLOG_GRID_STATE, savedUserSettings);
    });
  },

  addTagsAsColumnDefsFromColumnState({ commit }, userSettingsColumnState) {
    commit(SET_INSIGHTS_TAGS, userSettingsColumnState?.filter(col => col.colId.includes('tag:')).map(col => {
      const [, tagType, tagName] = col.colId.split(':');
      return {
        tagName,
        tagType
      };
    }));
  },

  setSelectedChartType({ commit }, chartType) {
    commit(SET_INSIGHT_SELECTED_CHART_TYPE, chartType);
  },

  setDefaultInsightActive({ commit }) {
    commit(SET_DEFAULT_INSIGHT_ACTIVE);
  },

  setDefaultCallLogActive({ commit }) {
    commit(SET_DEFAULT_CALLLOG_ACTIVE);
  },

  setSelectedGroupBys({ dispatch, commit }, { groupBys, columnState }) {
    dispatch('setGroupBySettings', { groupBys, columnState });
    commit(SET_SELECTED_GROUP_BY, groupBys[0]);
  },

  setGroupBySettings({ commit, getters }, { groupBys, columnState }) {
    const currentGridState = { ...getters.activeInsight.settings.groupSettings.gridState };
    const newRowGroupCols = [
      ...groupBys.map(groupBy => {
        return getValueObject({
          aggFunc: undefined,
          field: groupBy.field,
          id: groupBy.field,
          displayName: groupBy.displayName
        });
      })
    ];

    const groupBySettings = getInitialGroupingSettings(groupBys, columnState);
    const groupByChartData = getDefaultInsightData();

    groupBySettings.gridState = {
      ...groupBySettings.gridState,
      ...currentGridState,
      rowGroupCols: newRowGroupCols
    };

    commit(SET_GROUP_BY_INSIGHT_SETTINGS, { groupBySettings });
    commit(SET_GROUP_BY_INSIGHT_DATA, { dataObj: groupByChartData });
  },

  resetInsightSettings({ commit }) {
    commit(RESET_INSIGHT_SETTINGS);
  },

  // TODO: Consolidate setting Row groups
  updateBreakdownHeirarchy({ commit, dispatch, getters }, { groupBy, index }) {
    if (index === 0) {
      dispatch('changeBaseBreakdown', groupBy);
      return;
    }

    const oldBrekdownHierarchyCount = getters.activeInsightGroupHierarchy.length;

    commit(UPDATE_GRID_ROW_GROUPS, { groupBy, index });

    const newBrekdownHierarchyCount = getters.activeInsightGroupHierarchy.length;

    if (newBrekdownHierarchyCount < oldBrekdownHierarchyCount) {
      commit(CLEAR_ACTIVE_INSIGHT_EXPANDED_GROUP_IDS);
    }
  },

  changeBaseBreakdown({ getters, dispatch }, groupBy) {
    const currentGroups = [...getters.activeInsightGroupHierarchy];
    currentGroups.splice(0, 1, {
      id: groupBy.field,
      ...groupBy
    });

    dispatch('setSelectedGroupBys', { groupBys: currentGroups });

    dispatch('setRowGroups', currentGroups);
  },

  setRowGroups({ commit }, rowGroups) {
    commit(SET_GRID_ROW_GROUPS, rowGroups);
  },

  deleteBreakdownLevel({ commit }, index) {
    commit(DELETE_GRID_ROW_GROUP, index);
    commit(CLEAR_ACTIVE_INSIGHT_EXPANDED_GROUP_IDS);
  },

  setChartDrilldownState({ commit }, { drilleddownTo, chartType, groupBy }) {
    commit(SET_CHART_DRILLDOWN_STATE, { drilleddownTo, chartType, groupBy });
  },

  getChartDrilldownData({ commit, getters }, {
    drilldowns,
    chartPoint,
    chartType,
    initialGroupBy,
    data
  }) {
    const settings = getters.activeInsight.settings.groupSettings;
    const groupKeys = drilldowns.map(d => d.parentEntityName);

    if (settings.gridState.valueCols === null) {
      settings.gridState.valueCols = settings.chartStates.selectedParameters.map(p => ({
        field: p.name,
        aggFunc: p.aggFunc
      }));
    }

    commit(SET_CHART_LOADER_STATUS, { groupBy: initialGroupBy, chartType, showLoader: true });

    const specificRecords = reportingService.getRowsForGrouping(data, settings.gridState.rowGroupCols.map(col => {
      return {
        column: col.field
      };
    }), groupKeys);

    commit(SET_CHART_DRILLDOWN_DATA, { chartPoint, chartType, groupBy: initialGroupBy, aggregatedData: specificRecords });

    commit(SET_CHART_LOADER_STATUS, { groupBy: initialGroupBy, chartType, showLoader: false });
  },

  updateDrilldownData({ dispatch, getters }, { groupBy, data }) {
    if (!getters.activeInsightData) {
      return;
    }

    const columnDrilldownState = getters.activeInsight.settings.groupSettings.chartStates.column.drilleddownTo;
    const pieDrilldownState = getters.activeInsight.settings.groupSettings.chartStates.pie.drilleddownTo;

    const columnDrilleddownData = getters.activeInsightData.chartData.column.drilleddownData;
    const pieDrilleddownData = getters.activeInsightData.chartData.pie.drilleddownData;

    if (columnDrilldownState && columnDrilldownState.length && !isEmpty(columnDrilleddownData)) {
      const lastElement = last(columnDrilldownState);

      dispatch('getChartDrilldownData', {
        drilldowns: columnDrilldownState,
        chartType: 'column',
        initialGroupBy: groupBy,
        chartPoint: {
          id: lastElement.parentEntityName
        },
        data
      });
    }

    if (pieDrilldownState && pieDrilldownState.length && !isEmpty(pieDrilleddownData)) {
      const lastElement = last(pieDrilldownState);
      dispatch('getChartDrilldownData', {
        drilldowns: pieDrilldownState,
        chartType: 'pie',
        initialGroupBy: groupBy,
        chartPoint: {
          id: lastElement.parentEntityName
        },
        data
      });
    }
  },

  setChartDefaultData({ commit, getters }, { groupBy, chartType, data }) {
    const gridState = getters.activeInsight.settings.groupSettings.gridState;

    const columns = gridState.rowGroupCols.map(col => {
      return {
        column: col.field
      };
    });
    const specificRecords = reportingService.getRowsForGrouping(data, columns, []);
    const parsedRecords = specificRecords.map(record => Object.keys(record).reduce((result, key) => {
      // Transform into float all the fields except for the column name & the hash
      if (key !== 'agGridHash' && !columns.find((column) => column.column == key)) {
        result[key] = parseFloat(record[key]);
      } else {
        result[key] = record[key];
      }

      return result;
    }, {}));

    commit(SET_CHART_DATA, { groupBy, chartType, data: parsedRecords });
  },

  setReportName({ commit }, { reportType, name }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_NAME, name);
    } else {
      commit(SET_CALLLOG_NAME, name);
    }
  },

  exportInsightData({ commit }, params) {
    commit(SET_EXPORT_FOR_AG_GRID, {
      shouldExport: true,
      params
    });
  },

  refreshInsightGridData({ commit }) {
    commit(RELOAD_INSIGHTS_GRID_DATA);
  },

  updateInsightLastUpdatedTime({ commit }, lastUpdated) {
    commit(SET_INSIGHT_LAST_UPDATED_AT, lastUpdated);
  },

  setReportingTimezoneId({ commit }, { reportType, timeZoneId }) {
    if (isInsightReport(reportType)) {
      commit(SET_INSIGHT_TIMEZONE_ID, timeZoneId);
    } else {
      commit(SET_CALLLOG_TIMEZONE_ID, timeZoneId);
    }

    userSettingsService.getUserSettings()
      .then((userSettings) => {
        userSettings.timeZoneId = timeZoneId;
        return userSettingsService.saveUserSettings(userSettings);
      });
  },

  async getReportingTimezoneId({ commit, rootGetters, dispatch }, { reportType, reportId }) {
    const userSettings = await userSettingsService.getUserSettings();
    const timeZoneId = userSettings.timeZoneId || rootGetters.accountTimezoneId;

    if (reportType === INSIGHT_REPORT_TYPE && isDefaultReportId(reportId)) {
      commit(SET_INSIGHT_TIMEZONE_ID, timeZoneId);
      dispatch('refreshInsightGridData');
    }

    if (reportType === CALLLOG_REPORT_TYPE && isDefaultReportId(reportId)) {
      commit(SET_CALLLOG_TIMEZONE_ID, timeZoneId);
    }

    return;
  },

  async getCallLogHistogramData({ commit, rootState, getters }) {
    const filters = [
      ...reportingService.getFilterFromFilterCriteria(getters.activeCallLogFilterCriteria)
    ];

    const data = {
      ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      groupByColumns: [
        {
          column: getters.histogramTimeGroupInterval
        }
      ],
      valueColumns: [
        {
          column: 'callCount',
          aggregateFunction: 'count'
        }
      ],
      orderByColumns: [],
      dateHistogramKeys: 'epoch',
      formatTimeZone: getters.activeCallLogTranslatedTimeZoneId,
      filters,
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    commit(SET_CALLLOG_HISTOGRAM_LOADER_STATUS, true);

    return reportingService.loadHistogramData(rootState.currentAccountId, data, true).then(({ allRecords }) => {
      commit(SET_CALLLOG_HISTOGRAM_DATA, Object.freeze(allRecords));
    }).finally(() => {
      commit(SET_CALLLOG_HISTOGRAM_LOADER_STATUS, false);
    });
  },

  setHistogramTimeGroup({ commit }, timeGroup) {
    commit(SET_CALLLOG_HISTOGRAM_GROUPING, timeGroup);
  },

  setDontShowInsightsTourAgain() {
    setUserSettings('showInsightsTour');
  },

  setDontShowCallLogsTourAgain() {
    setUserSettings('showCallLogsTour');
  },

  async annotateTagToCalls({ rootState, dispatch }, { tag, callIds }) {
    await Promise.all(
      callIds.map(async (callId) => {
        await callAnnotationService.annotateCall(rootState.currentAccountId, callId, tag);
      })
    );
    dispatch('resetCallLogDetails');
  },

  async requestMultipleConversionAdjustment({ rootState, dispatch }, { data, callIds }) {
    await Promise.all(
      callIds.map(async (callId) => {
        const requestData = {
          inboundCallId: callId,
          ...data
        };

        await callsService.requestConversionAdjustment(rootState.currentAccountId, requestData);
      })
    );

    dispatch('resetCallLogDetails');
  },

  async requestConversionAdjustment({ rootState, dispatch }, { data, callId }) {
    const requestData = {
      inboundCallId: callId,
      ...data
    };

    await callsService.requestConversionOverride(rootState.currentAccountId, requestData);
    dispatch('resetCallLogDetails');
  },

  async multipleCallPaymentAdjustment({ rootState, dispatch }, { callIds, data }) {

    await callsService.callPaymentOverrideBulk(rootState.currentAccountId, callIds, data);

    dispatch('resetCallLogDetails');
  },

  async multipleCallPaymentAdjustmentByLogs({ rootState, dispatch }, { callLogs, data }) {

    await callsService.callPaymentOverrideBulkByLogs(rootState.currentAccountId, callLogs, data);

    dispatch('resetCallLogDetails');
  },

  async getCallTranscription({ rootState, commit }, callDetails) {
    const callId = callDetails.inboundCallId;
    try {
      const transcription = await callsService.getCallTranscription(rootState.currentAccountId, callId);
      callDetails = {
        transcriptionDetails: {
          ...transcription.data
        },
        ...callDetails
      };
    } catch {
      callDetails = {
        transcriptionDetails: {
          transcriptionStatus: STATUS_ERROR,
        },
        ...callDetails
      };
    }
    commit(SET_CALL_LOG_DETAILS, { callDetails: callDetails || {}, callId });
  },

  async transcribeMultipleCalls({ rootState, dispatch }, { callIds, resetDetails = true }) {
    await callsService.transcribeMultipleCalls(rootState.currentAccountId, callIds);

    if (resetDetails) {
      dispatch('resetCallLogDetails');
    }
  },

  async transcribeMultipleCallsByLogs({ rootState, dispatch }, { callLog }) {
    await callsService.transcribeMultipleCallsByLogs(rootState.currentAccountId, callLog);

    dispatch('resetCallLogDetails');
  },

  async callPaymentAdjustment({ rootState, dispatch }, { data, callId }) {
    const requestData = {
      inboundCallId: callId,
      ...data
    };

    await callsService.callPaymentOverride(rootState.currentAccountId, requestData);
    dispatch('resetCallLogDetails');
  },

  refreshCallLogGridData({ commit }) {
    commit(RELOAD_CALL_LOGS_GRID_DATA);
  },

  updateCallLogLastUpdatedTime({ commit }) {
    commit(SET_CALL_LOG_LAST_UPDATED_AT);
  },

  async getCallLogDetails({ rootState, commit, getters }, callId) {
    const cachedDetails = getters.activeCallLogData.callLogDetails[callId];
    if (!cachedDetails || cachedDetails.isLive) {
      const callDetails = await reportingService.getCallDetails(rootState.currentAccountId, {
        inboundCallIds: [callId],
        formatTimespans: true,
        formatPercentages: true,
        formatDateTime: true,
        formatTimeZone: getters.activeCallLogTranslatedTimeZoneId,
        viewAsPubId: getters.activeCallLogViewAsPubId,
        viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
        viewAsManagerId: getters.activeCallLogViewAsManagerId
      });
      commit(SET_CALL_LOG_DETAILS, { callDetails: callDetails || {}, callId });
    }
  },

  prependFilterCriteriaToCallLogs({ commit, getters }, { column, value, filterGroupName, type, isTag, originalTag }) {
    if (type === 'timespan') {
      value = moment.duration(value).asSeconds();
    } else if (type === 'boolean') {
      value = booleanFormatter(value)?.toLowerCase();
    }

    const filterCriteria = [];
    const tagId = isTag ? `${originalTag.tagType}:${originalTag.tagName}` : `${filterGroupName}:${column}`;

    if (!reportingService.checkIfFieldExistsInCriteria(getters.activeCallLogFilterCriteria, tagId)) {
      const newAndCondition = filterService.getNewAndFilterCondition();
      newAndCondition.tagCriteria[0] = {
        ...newAndCondition.tagCriteria[0],
        filterField: column,
        tagId,
        value
      };

      filterCriteria.push(newAndCondition);

      commit(PREPEND_AND_FILTER_CRITERIA_TO_CALL_LOG, filterCriteria);
    }

  },

  async getAutoCompleteValues({ getters, rootState, commit }, { tagName, reportType, value = '' }) {
    let data = {
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    if (reportType === INSIGHT_REPORT_TYPE) {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeInsightTimeRange, getters.activeInsightTranslatedTimeZoneId),
      };
    } else {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      };
    }

    const response = await reportingService.getFieldValuesForTimeRange(rootState.currentAccountId, tagName, value, data);

    if (value) {
      commit(APPEND_CALL_FIELD_AUTOCOMPLETE_VALUES, { fieldName: tagName, values: response.data.values});
    } else {
      commit(SET_CALL_FIELD_AUTOCOMPLETE_VALUES, { fieldName: tagName, values: response.data.values});
    }

  },

  async getTagAutoCompleteValues({ rootState, commit, getters }, { tagType, tagName, searchString, reportType }) {
    let data = {
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    if (reportType === INSIGHT_REPORT_TYPE) {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeInsightTimeRange, getters.activeInsightTranslatedTimeZoneId),
      };
    } else {
      data = {
        ...data,
        ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      };
    }

    const response = await tagLookupService.getTagValuesFromNewElasticSearch(rootState.currentAccountId, tagType, tagName, searchString, data);

    if (searchString) {
      commit(APPEND_TAG_AUTOCOMPLETE_VALUES, { tagType, tagName, values: response.data.values });
    } else {
      commit(SET_TAG_AUTOCOMPLETE_VALUES, { tagType, tagName, values: response.data.values });
    }
  },

  async getQuickFilterInsightsDataForCallLogs({ getters, rootState }, { groupBy, columns }) {
    const filters = [
      ...reportingService.getFilterFromFilterCriteria(getters.activeCallLogFilterCriteria)
    ];

    const apiRequestData = {
      ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      groupByColumns: [{
        column: groupBy.colId,
        displayName: groupBy.headerName
      }],
      valueColumns: columns ? columns.map(col => {
        return {
          column: col.colId,
          displayName: col.colDef.headerName,
          aggregateFunction: col.aggFunc
        };
      }) : [],
      orderByColumns: columns.filter(col => !!col.sort).map(sortedColumn => (
        { column: sortedColumn.colId, direction: sortedColumn.sort }
      )),
      formatTimespans: true,
      formatPercentages: true,
      generateRollups: true,
      maxResultsPerGroup: 1000, // Safely increase max because we're only requesting a single level
      filters,
      formatTimeZone: getters.activeCallLogTranslatedTimeZoneId,
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    return await reportingService.loadQuickInsightsData(rootState.currentAccountId, apiRequestData, false);
  },

  setCallLogsSectionVisibility({ commit }, { section, visible }) {
    commit(SET_CALL_LOGS_VISIBILITY_SECTION, { section, visible });

    userSettingsService.getUserSettings()
      .then((userSettings) => {
        userSettings.callLogsSectionVisibility = userSettings.callLogsSectionVisibility || {};
        userSettings.callLogsSectionVisibility[section] = visible;
        return userSettingsService.saveUserSettings(userSettings);
      });
  },

  getCallLogsSectionVisibility({ commit }) {
    userSettingsService.getUserSettings()
      .then((userSettings) => {
        if (userSettings.callLogsSectionVisibility) {
          for (const [section, visible] of Object.entries(userSettings.callLogsSectionVisibility)) {
            commit(SET_CALL_LOGS_VISIBILITY_SECTION, { section, visible });
          }
        }
      });
  },

  setCallLogsQuickInsightsDataCache({ commit }, { data, cacheKey, totalsRecord, partialResult, lastUpdated }) {
    // TODO: Create map for caching data for different group keys
    commit(SET_CALL_LOGS_QUICK_INSIGHTS_DATA_CACHE, { data, cacheKey, totalsRecord, partialResult, lastUpdated });
  },

  setCallLogsHistogramDataCache({ commit }, { data, cacheKey, partialResult, lastUpdated }) {
    // TODO: Create map for caching data for different group keys
    commit(SET_CALL_LOGS_HISTOGRAM_DATA_CACHE, { data, cacheKey, partialResult, lastUpdated });
  },

  setCallLogsViewAs({ commit, dispatch, getters }, viewAs) {
    commit(SET_CALL_LOGS_VIEW_AS, viewAs);

    if (!getters.activeCallLogViewAsBuyerId && !getters.activeCallLogViewAsPubId && !getters.activeCallLogViewAsManagerId) {
      dispatch('getCallLogDefaultGridStateToUserSettings');
    } else {
      commit(SET_CALLLOG_GRID_STATE, getDefaultCallLogsGridState(getters.role));
    }

    dispatch('resetCallLogDetails');

    commit(SET_CALLLOG_FILTER_CRITERIA, []);

    dispatch('getTags', CALLLOG_REPORT_TYPE);

    dispatch('refreshCallLogsData');
  },

  resetCallLogColumnState({ commit, getters }) {
    commit(SET_CALLLOG_GRID_STATE, getDefaultCallLogsGridState(getters.role));
  },

  resetCallLogDetails({ commit }) {
    commit(RESET_CALL_LOG_DETAILS);
  },

  async exportCallLog({ getters, dispatch }, makeAPICall = true ) {
    const dateFormat ='MMM D, YYYY';
    const filters = [
      ...reportingService.getFilterFromFilterCriteria(getters.activeCallLogFilterCriteria)
    ];

    const columns = getters.activeCallLog.settings.gridSettings.columnState.filter(col =>
      !col.hide && col.colId !== 'action'
    );

    const role = getters.role;

    const sortedColumns = getters.activeCallLog.settings.gridSettings.columnState.filter(col => !!col.sort);

    const reportName = getters.activeCallLog.name;
    const exportName = (reportName ? (reportName) : 'Call Logs') + (getters.activeCallLogTimeRange ? (' (' + getters.activeCallLogTimeRange[0].format(dateFormat) + ' - ' + getters.activeCallLogTimeRange[1].format(dateFormat) + ')') : '');

    const apiRequestData = {
      ...reportingService.getReportingDateRange(getters.activeCallLogTimeRange, getters.activeCallLogTranslatedTimeZoneId),
      valueColumns: columns ? columns.map(col => {
        const displayName = customPropsForNonAdminRoles[CALLLOG_REPORT_TYPE][role]?.[col.colId]?.headerName || specificColsProperties[col.colId]?.headerName;

        return {
          column: col.colId,
          displayName
        };
      }) : [],
      orderByColumns: sortedColumns.map(sortedColumn => (
        { column: sortedColumn.colId, direction: sortedColumn.sort }
      )),
      formatTimespans: true,
      formatPercentages: true,
      formatDateTime: true,
      generateRollups: true,
      filters,
      formatTimeZone: getters.activeCallLogTranslatedTimeZoneId,
      viewAsPubId: getters.activeCallLogViewAsPubId,
      viewAsBuyerId: getters.activeCallLogViewAsBuyerId,
      viewAsManagerId: getters.activeCallLogViewAsManagerId,
    };

    if (makeAPICall) {
      dispatch('exportFiles/exportCallLogsV2', { apiRequestData, exportName }, { root: true });
    } else {
      return apiRequestData;
    }

  },

  setCallLogExportLoaderVisibility({ commit }, visibility) {
    commit(SET_CALL_LOG_EXPORT_LOADER_VISIBILITY, visibility);
  },

  setCallLogQuickInsightsTab({ commit }, groupingColId) {
    commit(SET_CALLLOG_QUICK_INSIGHTS_TAB, groupingColId);
  }
};

export default actions;
